import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as assetsApi from '../api/assets';
import * as sitesApi from '../api/sites';
import * as savedViewsApi from '../api/savedViews';
import * as exportsApi from '../api/exports';

const PAGE_SIZE = 10;
// Sorting and "by site" grouping need to see every matching row, not just
// one page of 10 — fetch the full filtered set once per filter change and
// do sort/group/fault-filter/pagination client-side. store/assets.js
// already over-fetches up to 1000 internally for the same reason, so this
// cap matches that.
const FETCH_LIMIT = 1000;

export function isHealthy(status) {
  return status === 'available' || status === 'online';
}

export function compareValues(a, b) {
  if (a === null || a === undefined || a === '') return 1;
  if (b === null || b === undefined || b === '') return -1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function Fleet() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [assets, setAssets] = useState([]);
  const [sites, setSites] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    siteId: '',
    status: '',
    program: '',
    search: ''
  });
  const [savedViews, setSavedViews] = useState([]);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [exporting, setExporting] = useState(false);
  // No default sort field: leaving one active by default would decorate a
  // column header with a sort arrow on first load, changing its text (e.g.
  // "Site" -> "Site ▲") — sort only kicks in once the user clicks a header.
  const [sort, setSort] = useState({ field: null, direction: 'asc' });
  const [groupBySite, setGroupBySite] = useState(false);
  const [faultFilterActive, setFaultFilterActive] = useState(false);

  const loadSites = useCallback(async () => {
    if (!selectedOrg) return;
    try {
      const sitesList = await sitesApi.getSites(selectedOrg.id);
      setSites(sitesList);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  }, [selectedOrg]);

  const loadSavedViews = useCallback(async () => {
    if (!selectedOrg) return;
    try {
      const views = await savedViewsApi.getSavedViews(selectedOrg.id, 'fleet');
      setSavedViews(views);
    } catch (err) {
      console.error('Failed to load saved views:', err);
    }
  }, [selectedOrg]);

  const loadAssets = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await assetsApi.getAssets(selectedOrg.id, {
        siteId: filters.siteId || undefined,
        status: filters.status || undefined,
        program: filters.program || undefined,
        search: filters.search || undefined,
        limit: FETCH_LIMIT,
        offset: 0
      });
      setAssets(result.assets);
      setLastUpdated(result.lastUpdated);
      if (result.total > FETCH_LIMIT) {
        console.warn(`Fleet: org has ${result.total} matching assets, only showing the first ${FETCH_LIMIT}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filters]);

  useEffect(() => {
    loadSites();
    loadSavedViews();
  }, [loadSites, loadSavedViews]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  // Status values come straight through from live Asset API data now (e.g.
  // "available"), not just the mock set (online/offline/maintenance) — build
  // the filter dropdown from whatever's actually present instead of a fixed
  // stale list.
  const statusOptions = useMemo(() => {
    const values = new Set(assets.map(a => a.status).filter(Boolean));
    return Array.from(values).sort();
  }, [assets]);

  const faultCount = useMemo(
    () => assets.filter(a => !isHealthy(a.status)).length,
    [assets]
  );

  const visibleAssets = useMemo(() => {
    let result = faultFilterActive ? assets.filter(a => !isHealthy(a.status)) : assets;

    const { field, direction } = sort;
    if (field) {
      const dir = direction === 'desc' ? -1 : 1;
      result = [...result].sort((a, b) => dir * compareValues(a[field], b[field]));
    }

    return result;
  }, [assets, faultFilterActive, sort]);

  const groupedBySite = useMemo(() => {
    if (!groupBySite) return null;
    const groups = new Map();
    visibleAssets.forEach(asset => {
      const key = asset.siteId || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, { siteId: key, siteName: asset.siteName || 'Unknown site', items: [] });
      }
      groups.get(key).items.push(asset);
    });
    return Array.from(groups.values()).sort((a, b) => compareValues(a.siteName, b.siteName));
  }, [groupBySite, visibleAssets]);

  const totalPages = Math.ceil(visibleAssets.length / PAGE_SIZE);
  const pagedAssets = useMemo(
    () => visibleAssets.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [visibleAssets, page]
  );

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleSort = (field) => {
    setSort(prev => prev.field === field
      ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { field, direction: 'asc' });
    setPage(0);
  };

  const handleToggleFaultFilter = () => {
    setFaultFilterActive(prev => !prev);
    setPage(0);
  };

  const handleRowClick = (assetId) => {
    navigate(`/fleet/assets/${assetId}`);
  };

  const handleLoadView = (viewId) => {
    setSelectedViewId(viewId);
    if (!viewId) {
      setFilters({ siteId: '', status: '', program: '', search: '' });
      return;
    }
    const view = savedViews.find(v => v.id === viewId);
    if (view) {
      setFilters(view.filters || { siteId: '', status: '', program: '', search: '' });
      setPage(0);
    }
  };

  const handleSaveView = async () => {
    if (!newViewName.trim() || !selectedOrg) return;
    try {
      await savedViewsApi.createSavedView(selectedOrg.id, {
        name: newViewName.trim(),
        type: 'fleet',
        filters,
        columns: ['site', 'assetId', 'model', 'status', 'lastSeen', 'program']
      });
      setNewViewName('');
      setShowSaveModal(false);
      loadSavedViews();
    } catch (err) {
      console.error('Failed to save view:', err);
    }
  };

  const handleDeleteView = async (viewId) => {
    if (!selectedOrg) return;
    try {
      await savedViewsApi.deleteSavedView(selectedOrg.id, viewId);
      if (selectedViewId === viewId) {
        setSelectedViewId('');
      }
      loadSavedViews();
    } catch (err) {
      console.error('Failed to delete view:', err);
    }
  };

  const handleExport = async () => {
    if (!selectedOrg || exporting) return;
    setExporting(true);
    try {
      const exportFilters = {
        siteId: filters.siteId || undefined,
        status: filters.status || undefined,
        program: filters.program || undefined,
        search: filters.search || undefined
      };
      const exportJob = await exportsApi.createFleetExport(selectedOrg.id, exportFilters);

      if (exportJob.status === 'completed' && exportJob.artifactId) {
        const { blob, filename } = await exportsApi.downloadArtifact(selectedOrg.id, exportJob.artifactId);
        exportsApi.triggerDownload(blob, filename);
      }
    } catch (err) {
      console.error('Failed to export:', err);
      setError('Failed to export fleet data');
    } finally {
      setExporting(false);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view fleet assets.</p>
        </div>
      </div>
    );
  }

  const columns = [
    { key: 'siteName', label: 'Site', sortable: true, hideWhenGrouped: true },
    { key: 'name', label: 'Asset ID', sortable: true },
    { key: 'model', label: 'Model', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'lastSeen', label: 'Last Seen', sortable: true },
    { key: 'program', label: 'Program', sortable: true }
  ];
  const visibleColumns = groupBySite ? columns.filter(c => !c.hideWhenGrouped) : columns;

  const renderSortIndicator = (field) => {
    if (sort.field !== field) return null;
    return <span style={styles.sortArrow}>{sort.direction === 'asc' ? ' ▲' : ' ▼'}</span>;
  };

  const renderRow = (asset) => (
    <tr
      key={asset.id}
      style={styles.tr}
      onClick={() => handleRowClick(asset.id)}
      data-testid={`fleet-row-${asset.id}`}
    >
      {!groupBySite && <td style={styles.td}>{asset.siteName}</td>}
      <td style={styles.td}>
        <div>
          <div style={styles.assetName}>{asset.name}</div>
          <div style={styles.serialNumber}>{asset.serialNumber}</div>
        </div>
      </td>
      <td style={styles.td}>{asset.model}</td>
      <td style={styles.td}>
        <span style={{
          ...styles.statusBadge,
          background: getStatusColor(asset.status).bg,
          color: getStatusColor(asset.status).text
        }}>
          {asset.status}
        </span>
      </td>
      <td style={styles.td}>
        {asset.lastSeen ? new Date(asset.lastSeen).toLocaleString() : '-'}
      </td>
      <td style={styles.td}>
        <span style={styles.programTag}>{asset.program}</span>
      </td>
    </tr>
  );

  return (
    <div style={styles.container}>
      <PageHeader
        title="Fleet"
        subtitle={`${visibleAssets.length} assets in ${selectedOrg?.name}`}
      />

      <button
        type="button"
        onClick={handleToggleFaultFilter}
        style={{
          ...styles.faultPanel,
          ...(faultFilterActive ? styles.faultPanelActive : {})
        }}
        data-testid="fault-summary-panel"
        aria-pressed={faultFilterActive}
      >
        <span style={styles.faultCount}>{faultCount}</span>
        <span style={styles.faultLabel}>
          {faultCount === 1 ? 'charger needs attention' : 'chargers need attention'}
          {' '}(not available/online)
        </span>
        {faultFilterActive && <span style={styles.faultClear}>Showing filtered — click to clear</span>}
      </button>

      <div style={styles.savedViewsBar}>
        <select
          value={selectedViewId}
          onChange={(e) => handleLoadView(e.target.value)}
          style={styles.viewSelect}
          data-testid="saved-views-dropdown"
        >
          <option value="">Select a saved view...</option>
          {savedViews.map(view => (
            <option key={view.id} value={view.id}>{view.name}</option>
          ))}
        </select>
        <button
          onClick={() => setShowSaveModal(true)}
          style={styles.saveButton}
          data-testid="save-view-button"
        >
          Save Current View
        </button>
        {selectedViewId && (
          <button
            onClick={() => handleDeleteView(selectedViewId)}
            style={styles.deleteButton}
            data-testid="delete-view-button"
          >
            Delete View
          </button>
        )}
        <div style={styles.viewModeToggle}>
          <button
            type="button"
            onClick={() => setGroupBySite(false)}
            style={{ ...styles.viewModeButton, ...(!groupBySite ? styles.viewModeButtonActive : {}) }}
            data-testid="view-mode-list"
          >
            List
          </button>
          <button
            type="button"
            onClick={() => setGroupBySite(true)}
            style={{ ...styles.viewModeButton, ...(groupBySite ? styles.viewModeButtonActive : {}) }}
            data-testid="view-mode-site"
          >
            By Site
          </button>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          style={styles.exportButton}
          data-testid="export-button"
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <input
            type="text"
            placeholder="Search assets..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            style={styles.searchInput}
            data-testid="fleet-search"
          />
          <select
            value={filters.siteId}
            onChange={(e) => handleFilterChange('siteId', e.target.value)}
            style={styles.select}
            data-testid="fleet-site-filter"
          >
            <option value="">All Sites</option>
            {sites.map(site => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={styles.select}
            data-testid="fleet-status-filter"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status} style={{ textTransform: 'capitalize' }}>
                {status}
              </option>
            ))}
          </select>
          <select
            value={filters.program}
            onChange={(e) => handleFilterChange('program', e.target.value)}
            style={styles.select}
            data-testid="fleet-program-filter"
          >
            <option value="">All Programs</option>
            <option value="NEVI">NEVI</option>
            <option value="CFI">CFI</option>
            <option value="Fleet">Fleet</option>
            <option value="Public">Public</option>
          </select>
        </div>
        {lastUpdated && (
          <span style={styles.lastUpdated}>
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </span>
        )}
      </div>

      {showSaveModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="save-view-modal">
            <h3 style={styles.modalTitle}>Save View</h3>
            <input
              type="text"
              placeholder="View name..."
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              style={styles.modalInput}
              data-testid="view-name-input"
            />
            <div style={styles.modalActions}>
              <button onClick={() => setShowSaveModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button onClick={handleSaveView} style={styles.confirmButton} data-testid="confirm-save-view">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading fleet...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table} data-testid="fleet-table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th
                      key={col.key}
                      style={{ ...styles.th, cursor: col.sortable ? 'pointer' : 'default' }}
                      onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    >
                      {col.label}
                      {col.sortable && renderSortIndicator(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupBySite ? (
                  groupedBySite.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} style={styles.emptyState}>
                        No assets found
                      </td>
                    </tr>
                  ) : (
                    groupedBySite.map(group => (
                      <React.Fragment key={group.siteId}>
                        <tr>
                          <td colSpan={visibleColumns.length} style={styles.groupHeader}>
                            {group.siteName} <span style={styles.groupCount}>({group.items.length})</span>
                          </td>
                        </tr>
                        {group.items.map(renderRow)}
                      </React.Fragment>
                    ))
                  )
                ) : (
                  <>
                    {pagedAssets.map(renderRow)}
                    {pagedAssets.length === 0 && (
                      <tr>
                        <td colSpan={visibleColumns.length} style={styles.emptyState}>
                          No assets found
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {!groupBySite && totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                style={styles.pageButton}
              >
                Previous
              </button>
              <span style={styles.pageInfo}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                style={styles.pageButton}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    online: { bg: '#c6f6d5', text: '#276749' },
    available: { bg: '#c6f6d5', text: '#276749' },
    offline: { bg: '#fed7d7', text: '#c53030' },
    maintenance: { bg: '#fefcbf', text: '#975a16' },
    fault: { bg: '#fed7d7', text: '#c53030' }
  };
  return colors[status] || { bg: '#e2e8f0', text: '#4a5568' };
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  faultPanel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    width: '100%',
    padding: '1rem 1.25rem',
    marginBottom: '1rem',
    background: 'white',
    border: '1px solid #fed7d7',
    borderLeft: '4px solid #e53e3e',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  faultPanelActive: {
    background: '#fff5f5',
    borderColor: '#e53e3e'
  },
  faultCount: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#c53030'
  },
  faultLabel: {
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  faultClear: {
    marginLeft: 'auto',
    fontSize: '0.75rem',
    color: '#c53030',
    fontWeight: '500'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  filters: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  searchInput: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    width: '200px'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  lastUpdated: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568',
    userSelect: 'none'
  },
  sortArrow: {
    fontSize: '0.7rem'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0',
    cursor: 'pointer',
    transition: 'background 0.15s'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  groupHeader: {
    padding: '0.625rem 1rem',
    background: '#edf2f7',
    fontWeight: '600',
    fontSize: '0.8125rem',
    color: '#2d3748'
  },
  groupCount: {
    fontWeight: '400',
    color: '#718096'
  },
  assetName: {
    fontWeight: '500'
  },
  serialNumber: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  programTag: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    background: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  emptyState: {
    padding: '3rem',
    textAlign: 'center',
    color: '#718096'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '1rem',
    padding: '1rem'
  },
  pageButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    cursor: 'pointer',
    fontSize: '0.875rem'
  },
  pageInfo: {
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  savedViewsBar: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  viewSelect: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white',
    minWidth: '200px'
  },
  saveButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  deleteButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e53e3e',
    background: 'white',
    color: '#e53e3e',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  viewModeToggle: {
    display: 'flex',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  viewModeButton: {
    padding: '0.5rem 0.875rem',
    border: 'none',
    background: 'white',
    color: '#4a5568',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  viewModeButtonActive: {
    background: '#3182ce',
    color: 'white'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '300px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.125rem',
    fontWeight: '600'
  },
  modalInput: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    marginBottom: '1rem'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  confirmButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  exportButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #38a169',
    background: '#38a169',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer',
    marginLeft: 'auto'
  }
};
